/**
 * Cat Instinct - what the mode marks, as pure arithmetic.
 *
 * This is an ACCESSIBILITY feature, not an ability (DESIGN.md §8): no cooldown,
 * no meter, no cost, no unlock. A six-year-old holds one button and immediately
 * sees what can be touched, what evidence is still lying around, and where the
 * keys are. Anything here that starts to look like a resource to manage is a
 * design mistake, not a balance knob.
 *
 * Pure data and pure functions - no Three.js, no Rapier. The renderer-facing half
 * lives in src/render/instinctView.ts.
 */

import type { Vec3 } from '@/core/math';
import { distance } from '@/core/math';
import { tuning, type InstinctCategoryName } from '@/core/tuning';

export type InstinctCategory = InstinctCategoryName;

export const INSTINCT_CATEGORIES: readonly InstinctCategory[] = [
  'prop',
  'key',
  'trace',
  'hideSpot',
  'goal',
];

export interface InstinctCandidate {
  id: string;
  category: InstinctCategory;
  position: Vec3;
  /**
   * False for anything already used up - broken, collected, carried, resolved,
   * or a key that has not been revealed yet. Marking those would teach a child
   * to walk to things that do nothing.
   */
  eligible: boolean;
  /** Hide spots only: the cat is inside this one right now. */
  occupied?: boolean;
}

export interface InstinctMark {
  id: string;
  category: InstinctCategory;
  position: Vec3;
  /** Multiplies the base marker size. */
  scale: number;
}

/**
 * Everything but props reads through walls.
 *
 * Props are a "what can I touch right now" answer, so range and line of sight are
 * the point. Keys, traces, hide spots and goals are a "where do I go next"
 * answer, which is useless if a wall hides it - that is the whole reason the mode
 * exists.
 */
export function ignoresRange(category: InstinctCategory): boolean {
  return category !== 'prop';
}

/** Marks to draw this frame. Presentation only - never mutates gameplay state. */
export function selectInstinctMarks(
  candidates: readonly InstinctCandidate[],
  origin: Vec3,
  maxRange: number = tuning.instinct.maxHighlightRange,
): InstinctMark[] {
  const marks: InstinctMark[] = [];
  for (const candidate of candidates) {
    if (!candidate.eligible) continue;
    if (!ignoresRange(candidate.category) && distance(origin, candidate.position) > maxRange) {
      continue;
    }
    marks.push({
      id: candidate.id,
      category: candidate.category,
      position: candidate.position,
      scale: markScaleFor(candidate),
    });
  }
  return marks;
}

/**
 * How big a mark is, relative to the base marker size.
 *
 * An escape route is the most important thing on screen when heat is high, so it
 * gets the biggest mark. The spot the cat is already inside shrinks instead:
 * that is somewhere it IS, not somewhere it could run to, and drawing both at
 * the same weight would make the useful ones harder to pick out.
 *
 * Goals are the biggest of all, and they need to be: they share the palette's
 * win-state gold with keys, so size is the only thing telling a child "carry it
 * HERE" apart from "pick this up".
 */
export function markScaleFor(candidate: InstinctCandidate): number {
  if (candidate.category === 'goal') return tuning.instinct.goalMarkerScale;
  if (candidate.category !== 'hideSpot') return 1;
  return candidate.occupied
    ? tuning.instinct.hideSpotOccupiedScale
    : tuning.instinct.hideSpotMarkerScale;
}

/**
 * The simulation speed while the mode is held.
 *
 * Gated on `playing` so the catch cutaway and the win screen are never slowed by
 * a button the player happens to be leaning on.
 */
export function instinctTimeScale(params: { held: boolean; playing: boolean }): number {
  if (!params.held || !params.playing) return 1;
  return tuning.instinct.timeScale;
}

/** 0-1 pulse for marker scale and opacity, driven by REAL time, not sim time. */
export function instinctPulse(elapsedSeconds: number): number {
  const phase = elapsedSeconds * tuning.instinct.pulseHz * Math.PI * 2;
  return (Math.sin(phase) + 1) / 2;
}
